const TAWK_SCRIPT_ID = 'tawk-chat-script';
const TAWK_EMBED_URL =
  'https://embed.tawk.to/69c676fb157af41c494161a9/1jknk1hgf';

interface ITawkApi {
  hideWidget?: () => void;
  showWidget?: () => void;
  maximize?: () => void;
  onLoad?: () => void;
  setAttributes?: (
    attributes: {
      name?: string;
      email?: string;
      userId?: string;
    },
    callback?: (error?: unknown) => void,
  ) => void;
}

declare global {
  var Tawk_API: ITawkApi | undefined;
  var Tawk_LoadStart: Date | undefined;
}

let isTawkReady = false;
let shouldOpenOnLoad = false;
let isWidgetVisible = true;
let pendingUserAttributes:
  | {
      name?: string;
      email?: string;
      userId?: string;
    }
  | undefined;
let lastUserAttributesKey = '';

function ensureTawkApi(): ITawkApi {
  const tawkApi = globalThis.Tawk_API ?? {};
  globalThis.Tawk_API = tawkApi;
  return tawkApi;
}

function showAndMaximizeTawk(): void {
  globalThis.Tawk_API?.showWidget?.();
  globalThis.Tawk_API?.maximize?.();
}

function syncTawkWidgetVisibility(): void {
  if (isWidgetVisible) {
    globalThis.Tawk_API?.showWidget?.();
    return;
  }

  globalThis.Tawk_API?.hideWidget?.();
}

function applyPendingUserAttributes(): void {
  if (!pendingUserAttributes) {
    return;
  }

  const tawkApi = ensureTawkApi();

  if (!tawkApi.setAttributes) {
    return;
  }

  tawkApi.setAttributes(pendingUserAttributes, (error) => {
    if (error) {
      console.error('Tawk error:', error);
    }
  });
}

export function initTawkWidget(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const tawkApi = ensureTawkApi();

  tawkApi.onLoad = () => {
    isTawkReady = true;
    syncTawkWidgetVisibility();
    applyPendingUserAttributes();

    if (shouldOpenOnLoad) {
      shouldOpenOnLoad = false;
      showAndMaximizeTawk();
    }
  };

  if (document.getElementById(TAWK_SCRIPT_ID)) {
    return;
  }

  globalThis.Tawk_LoadStart = new Date();

  const script = document.createElement('script');
  script.id = TAWK_SCRIPT_ID;
  script.async = true;
  script.src = TAWK_EMBED_URL;
  script.charset = 'UTF-8';
  script.setAttribute('crossorigin', '*');

  const firstScript = document.getElementsByTagName('script')[0];

  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
    return;
  }

  document.head.appendChild(script);
}

export function setTawkUserAttributes(user: {
  id: number;
  name: string;
  email: string;
}): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (!user.id) {
    pendingUserAttributes = undefined;
    lastUserAttributesKey = '';
    return;
  }

  const nextKey = `${user.id}|${user.name}|${user.email}`;

  if (nextKey === lastUserAttributesKey) {
    return;
  }

  lastUserAttributesKey = nextKey;
  pendingUserAttributes = {
    name: user.name || undefined,
    email: user.email || undefined,
    userId: String(user.id),
  };

  if (!isTawkReady) {
    return;
  }

  applyPendingUserAttributes();
}

export function openTawkChat(): void {
  if (typeof window === 'undefined') {
    return;
  }

  isWidgetVisible = true;

  if (!isTawkReady) {
    shouldOpenOnLoad = true;
    initTawkWidget();
    return;
  }

  showAndMaximizeTawk();
}

export function hideTawkWidget(): void {
  isWidgetVisible = false;

  if (!isTawkReady) {
    return;
  }

  globalThis.Tawk_API?.hideWidget?.();
}

export function showTawkWidget(): void {
  isWidgetVisible = true;

  if (!isTawkReady) {
    return;
  }

  globalThis.Tawk_API?.showWidget?.();
}
